<template>
  <SmDrawer
    v-model:visibility="localShowDrawer"
    :title="drawerTitle"
    :before-close="handleClose">
    <template #default>
      <!-- licenceModelControl should be enabled, when the software does not already exist (is newly created) -->
      <!-- for the disabled name input, look into the computed below -->
      <ApplicationForm
        @cancel="localShowDrawer = false"
        @save="globalStore.closeAllDrawers()" />
    </template>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { ElMessageBox } from 'element-plus'
  import { cloneDeep } from 'lodash-es'

  import { useI18n } from 'vue-i18n'

  import { SoftwarePostIn, SoftwareService } from '@/client'
  import { ExtendedApplication, Application } from '@/types/software'
  import { useGlobalStore } from '@/stores/globalStore'

  const globalStore = useGlobalStore()

  // Props and Emits
  const emit = defineEmits([
    'update:showDrawer',
    'softwareDeleted',
    'closedDrawer',
  ])
  const props = defineProps({
    showDrawer: {
      type: Boolean,
      default: true,
    },
    customApplicationTemplate: {
      type: Object as PropType<SoftwarePostIn>,
      default: (): Application | undefined => {
        return undefined
      },
    },
    // used to enable/disable the name input of the EditsoftwareForm
    addingApplicationFromTemplate: {
      type: Boolean,
      default: false,
    },
    // used to show/hide the licenceModel controlin the EditSoftwareForm
    isAlreadyExistingApplication: {
      type: Boolean,
      default: false,
    },
    // used to keep the name input enabled if the software was created
    isNameEqualsToSearch: {
      type: Boolean,
      default: false,
    },
  })

  const i18n = useI18n()

  const localShowDrawer = computed({
    get: () => props.showDrawer,
    set: (value) => emit('update:showDrawer', value),
  })

  const applicationName = computed(() => {
    if (props.customApplicationTemplate != undefined) {
      return props.customApplicationTemplate.software_name
    } else return ''
  })

  const initialFormData = {
    _id: '',
    desc: '',
    desc_short: '',
    type: 'manually',
    icon: '',
    display_name: '',
    application_name: '',
    price: '',
    count: -1,
    software_licenses: {},
    data: {},
    isValid: false,
  } as ExtendedApplication

  // Custom software data
  const formData = ref<ExtendedApplication>(cloneDeep(initialFormData))

  // *** MISC ****
  // Ask if the User is sure that he want so close the Dialog
  const handleClose = () => {
    ElMessageBox.confirm(
      i18n.t('drawers.editSoftwareDrawer.beforeClose.text'),
      {
        confirmButtonText: i18n.t('general.yes'),
        cancelButtonText: i18n.t('general.no'),
      }
    )
      .then(() => {
        SoftwareService.getSoftwareListApiV1SoftwareSoftwareGet({}).then(
          (applications) => {
            const isAppExisting = applications.find(
              (app) => app.software_name === formData.value.application_name
            )

            if (isAppExisting)
              SoftwareService.deleteSoftwareApiV1SoftwareSoftwareSoftwareIdDelete(
                { softwareId: applicationName.value }
              )
          }
        )
        formData.value = cloneDeep(initialFormData)
        localShowDrawer.value = false
      })
      .catch(() => {
        // catch error
      })
  }

  /**
   * Compute the title of the Drawer
   */
  const drawerTitle = computed(() => {
    if (formData.value && formData.value.application_name) {
      return formData.value.application_name
    } else if (props.customApplicationTemplate !== undefined) {
      return props.customApplicationTemplate.software_name
    } else {
      return i18n.t('drawers.editSoftwareDrawer.manuallySoftwareTitle')
    }
  })
</script>

<style scoped>
  .header {
    margin-bottom: 1rem;
  }

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 3rem;
  }
</style>
